import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTrip } from '../contexts/TripContext';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { TripCard } from '../components/trip/TripCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, MapPin, Calendar, Search, Filter, X, 
  Loader2, SlidersHorizontal, ArrowUpDown
} from 'lucide-react';
import { format, parseISO, isAfter, isBefore, isWithinInterval } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { ViewModeToggle, useViewMode, getGridClassName, VIEW_MODES } from '../components/common/ViewModeToggle';

export default function TripsPage() {
  const { trips, fetchTrips, loading } = useTrip();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_asc');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useViewMode('trips-view-mode');

  // Translated status options
  const STATUS_OPTIONS = [
    { value: 'all', label: t('common.all') },
    { value: 'planning', label: t('trips.status.planning') },
    { value: 'confirmed', label: t('trips.status.confirmed') },
    { value: 'ongoing', label: t('trips.status.ongoing') },
    { value: 'completed', label: t('trips.status.completed') },
    { value: 'cancelled', label: t('trips.status.cancelled') },
  ];

  const DATE_FILTER_OPTIONS = [
    { value: 'all', label: language === 'en-US' ? 'All dates' : 'Todas as datas' },
    { value: 'future', label: language === 'en-US' ? 'Future trips' : 'Viagens futuras' },
    { value: 'past', label: language === 'en-US' ? 'Past trips' : 'Viagens passadas' },
    { value: 'thisMonth', label: language === 'en-US' ? 'This month' : 'Este mês' },
    { value: 'thisYear', label: language === 'en-US' ? 'This year' : 'Este ano' },
  ];

  const SORT_OPTIONS = [
    { value: 'date_asc', label: language === 'en-US' ? 'Date (nearest)' : 'Data (mais próxima)' },
    { value: 'date_desc', label: language === 'en-US' ? 'Date (farthest)' : 'Data (mais distante)' },
    { value: 'name_asc', label: language === 'en-US' ? 'Name (A-Z)' : 'Nome (A-Z)' },
    { value: 'name_desc', label: language === 'en-US' ? 'Name (Z-A)' : 'Nome (Z-A)' },
    { value: 'created_desc', label: language === 'en-US' ? 'Created (recent)' : 'Criação (mais recente)' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'confirmed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'ongoing': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'completed': return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
      case 'cancelled': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  const getStatusLabel = (status) => {
    return t(`trips.status.${status}`) || status;
  };

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  // Filter and sort trips
  const filteredTrips = trips
    .filter(trip => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesName = trip.name?.toLowerCase().includes(search);
        const matchesDestination = trip.destination?.toLowerCase().includes(search);
        if (!matchesName && !matchesDestination) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && trip.status !== statusFilter) {
        return false;
      }

      // Date filter
      if (dateFilter !== 'all') {
        const startDate = typeof trip.start_date === 'string' ? parseISO(trip.start_date) : trip.start_date;
        const endDate = typeof trip.end_date === 'string' ? parseISO(trip.end_date) : trip.end_date;
        const now = new Date();

        switch (dateFilter) {
          case 'future':
            if (!isAfter(startDate, now)) return false;
            break;
          case 'past':
            if (!isBefore(endDate, now)) return false;
            break;
          case 'thisMonth':
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            if (!isWithinInterval(startDate, { start: monthStart, end: monthEnd })) return false;
            break;
          case 'thisYear':
            const yearStart = new Date(now.getFullYear(), 0, 1);
            const yearEnd = new Date(now.getFullYear(), 11, 31);
            if (!isWithinInterval(startDate, { start: yearStart, end: yearEnd })) return false;
            break;
          default:
            break;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = typeof a.start_date === 'string' ? parseISO(a.start_date) : a.start_date;
      const dateB = typeof b.start_date === 'string' ? parseISO(b.start_date) : b.start_date;

      switch (sortBy) {
        case 'date_asc':
          return dateA - dateB;
        case 'date_desc':
          return dateB - dateA;
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'created_desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return 0;
      }
    });

  // Count by status
  const statusCounts = {
    all: trips.length,
    planning: trips.filter(t => t.status === 'planning').length,
    confirmed: trips.filter(t => t.status === 'confirmed').length,
    ongoing: trips.filter(t => t.status === 'ongoing').length,
    completed: trips.filter(t => t.status === 'completed').length,
    cancelled: trips.filter(t => t.status === 'cancelled').length,
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter('all');
    setSortBy('date_asc');
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || dateFilter !== 'all';

  return (
    <DashboardLayout title={t('trips.title')}>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          {/* Desktop layout */}
          <div className="hidden sm:flex sm:items-center sm:justify-between">
            <div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 dark:text-white">
                {t('dashboard.myTrips')}
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                {trips.length} {trips.length === 1 ? (language === 'en-US' ? 'trip' : 'viagem') : (language === 'en-US' ? 'trips' : 'viagens')}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
              <Link to="/trips/new">
                <Button className="rounded-full bg-orange-500 hover:bg-orange-600" data-testid="create-trip-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard.newTrip')}
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Mobile layout - Title centered, controls below */}
          <div className="sm:hidden">
            <h1 className="font-heading font-bold text-2xl text-slate-900 dark:text-white text-center">
              Minhas Viagens
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-center">
              {trips.length} {trips.length === 1 ? 'viagem' : 'viagens'} no total
            </p>
            <div className="flex items-center justify-center gap-3 mt-3">
              <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
              <Link to="/trips/new">
                <Button className="rounded-full bg-orange-500 hover:bg-orange-600" data-testid="create-trip-btn-mobile">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova viagem
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Search and Filters */}
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar por nome ou destino..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-trips"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-orange-50 border-orange-300 text-orange-600 dark:bg-orange-900/20' : ''}
              data-testid="toggle-filters"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filtros
              {hasActiveFilters && (
                <Badge className="ml-2 bg-orange-500 text-white">
                  {[statusFilter !== 'all', dateFilter !== 'all', searchTerm].filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <Card>
                  <CardContent className="p-4">
                    <div className="grid sm:grid-cols-3 gap-4">
                      {/* Status Filter */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                          Status
                        </label>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                          <SelectTrigger data-testid="status-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                                {statusCounts[option.value] !== undefined && (
                                  <span className="ml-2 text-slate-400">({statusCounts[option.value]})</span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date Filter */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                          Período
                        </label>
                        <Select value={dateFilter} onValueChange={setDateFilter}>
                          <SelectTrigger data-testid="date-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DATE_FILTER_OPTIONS.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Sort */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                          Ordenar por
                        </label>
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger data-testid="sort-by">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SORT_OPTIONS.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {hasActiveFilters && (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          <X className="w-4 h-4 mr-2" />
                          Limpar filtros
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Status Quick Filters */}
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.filter(s => statusCounts[s.value] > 0 || s.value === 'all').map(option => (
            <button
              key={option.value}
              onClick={() => setStatusFilter(option.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                statusFilter === option.value
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
              data-testid={`quick-filter-${option.value}`}
            >
              {option.label}
              <span className="ml-1.5 opacity-70">({statusCounts[option.value]})</span>
            </button>
          ))}
        </div>

        {/* Trips Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : filteredTrips.length === 0 ? (
          <Card className="border-slate-100 dark:border-slate-700 border-dashed">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
                {hasActiveFilters ? 'Nenhuma viagem encontrada' : 'Nenhuma viagem ainda'}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {hasActiveFilters
                  ? 'Tente ajustar os filtros para ver mais resultados'
                  : 'Crie sua primeira viagem para começar'}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              ) : (
                <Link to="/trips/new">
                  <Button className="rounded-full bg-orange-500 hover:bg-orange-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar viagem
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Results count */}
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Mostrando {filteredTrips.length} de {trips.length} viagens
            </p>

            <div className={getGridClassName(viewMode)}>
              <AnimatePresence>
                {filteredTrips.map((trip, index) => (
                  <TripCard key={trip.id} trip={trip} index={index} viewMode={viewMode} />
                ))}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
